import { useHistory, useLocation } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { t } from 'ttag';

import { useAppContext } from '@contexts/AppContext';
import { useRegion } from '@hook/useRegion';
import { getOutletAbsoluteUrl, getShafaAbsoluteUrl } from '@utils/common';
import { PageMetaTitle } from '@components/PageMetaTitle';
import { PageMetaDescription } from '@components/PageMetaDescription';

import {
  Product,
  Review,
} from './types';
import {
  getOgDescription,
  getOgTitle,
  getStructuredDataMarkup,
} from './utils';

interface Props {
  metaTitle: string,
  metaDescription: string,
  product: Product,
  reviews: Review[],
}

export const ProductDetailsMeta: React.FC<Props> = ({
  metaTitle,
  metaDescription,
  product,
  reviews,
}) => {
  const history = useHistory();
  const location = useLocation();

  const { currencyName } = useRegion();

  const { isOutlet } = useAppContext();

  const structuredDataMarkup = getStructuredDataMarkup({
    product,
    reviews,
    isOutlet,
  });

  const siteName = isOutlet ? t`Шафа Аутлет` : t`Шафа`;
  const localizedPathname = history.createHref({ pathname: location.pathname });
  const ogTitle = getOgTitle({
    product,
    currencyName,
    siteName,
  });
  const ogDescription = getOgDescription({
    product,
    currencyName,
    siteName,
  });

  return (
    <>
      <PageMetaTitle
        title={metaTitle}
        ogTitle={ogTitle}
      />
      <PageMetaDescription
        description={metaDescription}
        ogDescription={ogDescription}
      />

      <Helmet>
        <meta property="al:ios:url" content={getShafaAbsoluteUrl(localizedPathname)} />
        <meta property="al:ios:app_store_id" content="1402020028" />
        <meta property="al:ios:app_name" content="Shafa" />
        <meta property="al:android:url" content={getShafaAbsoluteUrl(localizedPathname)} />
        <meta property="al:android:package" content="shafa.odejda.obuv.aksessuary" />
        <meta property="al:android:app_name" content="Shafa" />

        <meta property="og:site_name" content={isOutlet ? t`Шафа Аутлет` : t`Шафа`} />
        <meta property="og:type" content="product" />

        {!!product.brand && (
          <meta property="og:brand" content={product.brand.name} />
        )}

        {!!product.price && (
          <>
            <meta property="og:price:amount" content={String(product.price)} />
            <meta property="og:price:currency" content="UAH" />
          </>
        )}

        <meta property="og:availability" content="instock"/>
        <meta property="og:url" content={isOutlet ? getOutletAbsoluteUrl(localizedPathname) : getShafaAbsoluteUrl(localizedPathname)} />
        <meta property="og:image" content={product.photos[0].originalUrl} />

        <meta name="twitter:card" content="summary"/>
        <meta name="twitter:image" content={product.photos[0].originalUrl} />

        <link rel="image_src" href={product.photos[0].originalUrl} />
        <link rel="canonical" href={isOutlet ? getOutletAbsoluteUrl(localizedPathname) : getShafaAbsoluteUrl(localizedPathname)} />


        <script type='application/ld+json'>
          {JSON.stringify(structuredDataMarkup)}
        </script>
      </Helmet>
    </>
  );
};
